import React, { useEffect, useState, useMemo } from "react";
import PolicyTimeline from "@/components/clients/PolicyTimeline";
import OneChatPanel from "@/components/clients/OneChatPanel";
import DiscoveredPanel from "@/components/clients/DiscoveredPanel";
import Customer360Card from "@/components/clients/Customer360Card";
import CardLensPanel from "@/components/cardlens/CardLensPanel";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Briefcase, Calendar, Plus, Trash2, Edit2, Loader2, AlertTriangle, Download, ShieldAlert, Heart, Car, Sparkles, TrendingDown, TrendingUp as TrendingUpIcon, Wallet, FileText, ShoppingBag, Radar } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, formatINRShort } from "@/lib/api";
import NeedAnalysisWizard from "@/components/NeedAnalysisWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "discovered", label: "Discovered" },
  { key: "timeline", label: "Timeline" },
  { key: "appchat", label: "App Chat" },
  { key: "loans", label: "Loans" },
  { key: "cards", label: "Cards" },
  { key: "insurance", label: "Insurance" },
  { key: "investments", label: "Investments" },
  { key: "assets", label: "Assets" },
  { key: "family", label: "Family" },
  { key: "crece", label: "Crece Products" },
];

const ASSET_KINDS = [
  { k: "property", label: "Property" },
  { k: "vehicle", label: "Vehicle" },
  { k: "gold", label: "Gold / Jewellery" },
  { k: "fd", label: "Fixed Deposit" },
  { k: "mutual_fund", label: "Mutual Fund" },
  { k: "equity", label: "Equity / Stocks" },
  { k: "crypto", label: "Crypto" },
  { k: "savings", label: "Savings / Cash" },
  { k: "other", label: "Other" },
];

const INSURANCE_CATEGORIES = ["Life", "Health", "Motor", "Other"];
const INSURANCE_CATEGORY_MAP = {
  Life: "Life", Term: "Life", ULIP: "Life",
  Health: "Health",
  Motor: "Motor",
  Travel: "Other", Home: "Other", Other: "Other",
};
function bucketOf(policyType) { return INSURANCE_CATEGORY_MAP[policyType] || "Other"; }

const LOAN_TYPES = ["Home", "Personal", "Auto", "Education", "Business", "LAP", "Credit Card EMI", "Gold", "Other"];
const ASSET_BACKED_LOAN_TYPES = new Set(["Home", "Auto", "LAP", "Gold"]);
const INSURANCE_TYPES = ["Term", "Life", "Health", "Motor", "ULIP", "Travel", "Home", "Other"];
const INVESTMENT_TYPES = ["MF", "FD", "RD", "PPF", "EPF", "NPS", "Stocks", "Bonds", "Gold", "Real Estate", "Other"];
const FREQS = ["Monthly", "Quarterly", "Half-Yearly", "Yearly", "One-time"];
const RELATIONS = ["spouse", "son", "daughter", "father", "mother", "sibling", "other"];

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wide text-stone-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const REWARD_TYPES = ["cashback", "points", "miles", "fuel", "none"];

function CardForm({ initial, family, onSave, onCancel, saving }) {
  const [f, setF] = useState({ issuer: "", card_name: "", last4: "", credit_limit: "", billing_day: "", due_day: "", annual_fee: "", annual_fee_date: "", reward_type: "points", status: "active", family_member_id: "", notes: "", ...(initial || {}) });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const submit = () => {
    if (!f.issuer.trim()) return toast.error("Issuer is required");
    onSave({ ...f, credit_limit: num(f.credit_limit), billing_day: num(f.billing_day), due_day: num(f.due_day), annual_fee: num(f.annual_fee), annual_fee_date: f.annual_fee_date || null, family_member_id: f.family_member_id || null });
  };
  return (
    <div className="space-y-3" data-testid="card-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Issuer *"><Input value={f.issuer} onChange={(e) => set("issuer", e.target.value)} placeholder="HDFC Bank" data-testid="card-issuer" /></Field>
        <Field label="Card name"><Input value={f.card_name || ""} onChange={(e) => set("card_name", e.target.value)} placeholder="Regalia Gold" data-testid="card-name" /></Field>
        <Field label="Last 4 digits"><Input value={f.last4 || ""} onChange={(e) => set("last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="card-last4" /></Field>
        <Field label="Credit limit (₹)"><Input type="number" value={f.credit_limit ?? ""} onChange={(e) => set("credit_limit", e.target.value)} data-testid="card-limit" /></Field>
        <Field label="Billing day"><Input type="number" min="1" max="31" value={f.billing_day ?? ""} onChange={(e) => set("billing_day", e.target.value)} /></Field>
        <Field label="Due day"><Input type="number" min="1" max="31" value={f.due_day ?? ""} onChange={(e) => set("due_day", e.target.value)} /></Field>
        <Field label="Annual fee (₹)"><Input type="number" value={f.annual_fee ?? ""} onChange={(e) => set("annual_fee", e.target.value)} /></Field>
        <Field label="Annual fee date"><Input type="date" value={f.annual_fee_date || ""} onChange={(e) => set("annual_fee_date", e.target.value)} data-testid="card-fee-date" /></Field>
        <Field label="Reward type"><select value={f.reward_type || "points"} onChange={(e) => set("reward_type", e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm">{REWARD_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
        <Field label="Status"><select value={f.status || "active"} onChange={(e) => set("status", e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm">{["active", "closed", "blocked"].map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
        <Field label="Owner" className="col-span-2"><select value={f.family_member_id || ""} onChange={(e) => set("family_member_id", e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm"><option value="">Primary client</option>{family.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.relation})</option>)}</select></Field>
      </div>
      <Field label="Notes"><Input value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="card-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></div>
    </div>
  );
}

function LoanForm({ initial, family, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    bank: "", loan_type: "Home", account_no: "", sanctioned_amount: "", outstanding: "",
    asset_value: "",
    emi: "", emi_day: "", tenure_months: "", start_date: "", end_date: "", interest_rate: "", notes: "",
    family_member_id: "",
    ...(initial || {}),
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isAssetBacked = ASSET_BACKED_LOAN_TYPES.has(form.loan_type);
  const submit = () => {
    if (!form.bank.trim()) { toast.error("Bank is required"); return; }
    if (!form.loan_type) { toast.error("Loan type is required"); return; }
    const body = { ...form };
    ["sanctioned_amount", "outstanding", "asset_value", "emi", "emi_day", "tenure_months", "interest_rate"].forEach((k) => {
      if (body[k] === "" || body[k] === null || body[k] === undefined) delete body[k];
      else body[k] = Number(body[k]);
    });
    if (body.family_member_id === "") body.family_member_id = null;
    onSave(body);
  };
  // Helpful asset label per loan type
  const assetLabel = {
    Home: "House market value (₹)",
    Auto: "Vehicle current value (₹)",
    LAP: "Property current value (₹)",
    Gold: "Gold current value (₹)",
  }[form.loan_type] || "Asset value (₹)";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <Field label="Bank / Lender *"><Input value={form.bank} onChange={(e) => set("bank", e.target.value)} data-testid="loan-bank" /></Field>
      <Field label="Loan Type *">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.loan_type} onChange={(e) => set("loan_type", e.target.value)} data-testid="loan-type">
          {LOAN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Owner">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.family_member_id || ""} onChange={(e) => set("family_member_id", e.target.value || null)}>
          <option value="">Primary client</option>
          {family.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.relation})</option>)}
        </select>
      </Field>
      <Field label="Account no."><Input value={form.account_no || ""} onChange={(e) => set("account_no", e.target.value)} /></Field>
      <Field label="Sanctioned amount (₹)"><Input type="number" value={form.sanctioned_amount} onChange={(e) => set("sanctioned_amount", e.target.value)} /></Field>
      <Field label="Outstanding (₹)"><Input type="number" value={form.outstanding} onChange={(e) => set("outstanding", e.target.value)} /></Field>
      {isAssetBacked && (
        <Field label={assetLabel} className="sm:col-span-2">
          <Input type="number" value={form.asset_value} onChange={(e) => set("asset_value", e.target.value)}
            placeholder={form.sanctioned_amount ? `Defaults to ₹${Number(form.sanctioned_amount).toLocaleString("en-IN")} (sanctioned amount)` : "Current market value — counts towards net worth"}
            data-testid="loan-asset-value" />
          <div className="text-[11px] text-stone-500 mt-1">Current market value of the {form.loan_type.toLowerCase()}. Counts toward the client&apos;s net worth.</div>
        </Field>
      )}
      <Field label="EMI (₹)"><Input type="number" value={form.emi} onChange={(e) => set("emi", e.target.value)} /></Field>
      <Field label="EMI day (1-28)"><Input type="number" min={1} max={28} value={form.emi_day} onChange={(e) => set("emi_day", e.target.value)} /></Field>
      <Field label="Tenure (months)"><Input type="number" value={form.tenure_months} onChange={(e) => set("tenure_months", e.target.value)} /></Field>
      <Field label="Interest rate (%)"><Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} /></Field>
      <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
      <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></Field>
      <Field label="Notes" className="sm:col-span-2"><Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-loan-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function InsuranceForm({ initial, family, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    provider: "", policy_type: "Term", policy_no: "", sum_assured: "", premium: "",
    premium_frequency: "Yearly", due_date: "", start_date: "", end_date: "", nominee: "", notes: "",
    family_member_id: "",
    ...(initial || {}),
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.provider.trim()) { toast.error("Provider is required"); return; }
    const body = { ...form };
    ["sum_assured", "premium"].forEach((k) => {
      if (body[k] === "" || body[k] === null || body[k] === undefined) delete body[k];
      else body[k] = Number(body[k]);
    });
    if (body.family_member_id === "") body.family_member_id = null;
    onSave(body);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <Field label="Provider *"><Input value={form.provider} onChange={(e) => set("provider", e.target.value)} data-testid="ins-provider" /></Field>
      <Field label="Policy Type *">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.policy_type} onChange={(e) => set("policy_type", e.target.value)} data-testid="ins-type">
          {INSURANCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Owner">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.family_member_id || ""} onChange={(e) => set("family_member_id", e.target.value || null)}>
          <option value="">Primary client</option>
          {family.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.relation})</option>)}
        </select>
      </Field>
      <Field label="Policy no."><Input value={form.policy_no || ""} onChange={(e) => set("policy_no", e.target.value)} /></Field>
      <Field label="Sum assured (₹)"><Input type="number" value={form.sum_assured} onChange={(e) => set("sum_assured", e.target.value)} /></Field>
      <Field label="Premium (₹)"><Input type="number" value={form.premium} onChange={(e) => set("premium", e.target.value)} /></Field>
      <Field label="Frequency">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.premium_frequency || ""} onChange={(e) => set("premium_frequency", e.target.value)}>
          <option value="">—</option>
          {FREQS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Next due date"><Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="ins-due-date" /></Field>
      <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
      <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></Field>
      <Field label="Nominee"><Input value={form.nominee || ""} onChange={(e) => set("nominee", e.target.value)} /></Field>
      <Field label="Notes" className="sm:col-span-2"><Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-insurance-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function InvestmentForm({ initial, family, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    instrument: "MF", provider: "", folio_no: "", amount_invested: "", current_value: "",
    sip_amount: "", sip_day: "", maturity_date: "", start_date: "", notes: "",
    family_member_id: "",
    ...(initial || {}),
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    const body = { ...form };
    ["amount_invested", "current_value", "sip_amount", "sip_day"].forEach((k) => {
      if (body[k] === "" || body[k] === null || body[k] === undefined) delete body[k];
      else body[k] = Number(body[k]);
    });
    if (body.family_member_id === "") body.family_member_id = null;
    onSave(body);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <Field label="Instrument *">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.instrument} onChange={(e) => set("instrument", e.target.value)} data-testid="inv-instrument">
          {INVESTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Provider / Scheme"><Input value={form.provider || ""} onChange={(e) => set("provider", e.target.value)} data-testid="inv-provider" /></Field>
      <Field label="Owner">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.family_member_id || ""} onChange={(e) => set("family_member_id", e.target.value || null)}>
          <option value="">Primary client</option>
          {family.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.relation})</option>)}
        </select>
      </Field>
      <Field label="Folio / Account no."><Input value={form.folio_no || ""} onChange={(e) => set("folio_no", e.target.value)} /></Field>
      <Field label="Amount invested (₹)"><Input type="number" value={form.amount_invested} onChange={(e) => set("amount_invested", e.target.value)} /></Field>
      <Field label="Current value (₹)"><Input type="number" value={form.current_value} onChange={(e) => set("current_value", e.target.value)} /></Field>
      <Field label="SIP amount (₹)"><Input type="number" value={form.sip_amount} onChange={(e) => set("sip_amount", e.target.value)} /></Field>
      <Field label="SIP day (1-28)"><Input type="number" min={1} max={28} value={form.sip_day} onChange={(e) => set("sip_day", e.target.value)} /></Field>
      <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
      <Field label="Maturity date"><Input type="date" value={form.maturity_date || ""} onChange={(e) => set("maturity_date", e.target.value)} /></Field>
      <Field label="Notes" className="sm:col-span-2"><Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-investment-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function FamilyForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ name: "", relation: "spouse", dob: "", mobile: "", email: "", occupation: "", notes: "", ...(initial || {}) }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    onSave(form);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="fam-name" /></Field>
      <Field label="Relation *">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.relation} onChange={(e) => set("relation", e.target.value)} data-testid="fam-relation">
          {RELATIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="DOB"><Input type="date" value={form.dob || ""} onChange={(e) => set("dob", e.target.value)} /></Field>
      <Field label="Mobile"><Input value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
      <Field label="Email"><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
      <Field label="Occupation"><Input value={form.occupation || ""} onChange={(e) => set("occupation", e.target.value)} /></Field>
      <Field label="Notes" className="sm:col-span-2"><Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-family-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function AssetForm({ initial, family, loans, onSave, onCancel, saving }) {
  const [form, setForm] = React.useState({
    kind: "property",
    family_member_id: "",
    name: "",
    value: "",
    purchase_value: "",
    purchase_date: "",
    linked_loan_id: "",
    is_collateral: false,
    location: "",
    notes: "",
    ...(initial || {}),
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = () => {
    if (!form.name?.trim()) { toast.error("Asset name is required"); return; }
    const body = { ...form };
    body.value = Number(body.value || 0);
    body.purchase_value = body.purchase_value ? Number(body.purchase_value) : null;
    if (body.family_member_id === "") body.family_member_id = null;
    if (body.linked_loan_id === "") body.linked_loan_id = null;
    onSave(body);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <Field label="Kind *">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="asset-kind">
          {ASSET_KINDS.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
        </select>
      </Field>
      <Field label="Owner">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.family_member_id || ""} onChange={(e) => set("family_member_id", e.target.value || null)} data-testid="asset-owner">
          <option value="">Primary client</option>
          {family.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.relation})</option>)}
        </select>
      </Field>
      <Field label="Name / Description *" className="sm:col-span-2">
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="asset-name" placeholder="e.g. 3 BHK Andheri Flat / Honda City 2022 / SBI FD #4521" />
      </Field>
      <Field label="Current Value (₹) *">
        <Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="asset-value" />
      </Field>
      <Field label="Purchase Value (₹)">
        <Input type="number" value={form.purchase_value} onChange={(e) => set("purchase_value", e.target.value)} />
      </Field>
      <Field label="Purchase Date">
        <Input type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date", e.target.value)} />
      </Field>
      <Field label="Location / Storage">
        <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="City / locker / DP" />
      </Field>
      <Field label="Linked Loan (if collateralised)" className="sm:col-span-2">
        <select className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm" value={form.linked_loan_id || ""} onChange={(e) => set("linked_loan_id", e.target.value || null)} data-testid="asset-linked-loan">
          <option value="">— Not linked —</option>
          {(loans || []).map((l) => <option key={l.id} value={l.id}>{l.bank} · {l.loan_type} · ₹{Math.round(l.sanctioned_amount || 0).toLocaleString("en-IN")}</option>)}
        </select>
      </Field>
      <Field label="Notes" className="sm:col-span-2">
        <Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </Field>
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800" data-testid="save-asset-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}



function FormModal({ open, title, description, onClose, children }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDelete({ open, title, onCancel, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ownerName(item, family) {
  if (!item.family_member_id) return "Primary client";
  const f = family.find((x) => x.id === item.family_member_id);
  return f ? `${f.name} (${f.relation})` : "Family member";
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "dashboard");
  const [showCardLens, setShowCardLens] = useState(false);
  const [c360Key, setC360Key] = useState(0);
  const [insuranceFilter, setInsuranceFilter] = useState("All");
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(null); // { kind, item? }
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // { kind, id, label }
  const [naOpen, setNaOpen] = useState(false);

  const load = async () => {
    try {
      const [d, ins] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/insights`),
      ]);
      setData(d.data);
      setInsights(ins.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load client");
      navigate("/app/clients");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!data) {
    return <div className="text-center py-20 text-stone-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const { client, family, loans, insurance, investments, assets, crece_products } = data;
  const cards = data.cards || [];
  const assetsList = assets || [];

  const downloadReport = async () => {
    setDownloading(true);
    try {
      // Cache-buster (t=timestamp) forces CDN/proxies to fetch a fresh PDF instead of returning a cached one.
      const res = await api.get(`/clients/${id}/report.pdf?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `crece-snapshot-${client.name?.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "client"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error("Failed to generate report");
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async (kind, body) => {
    setSaving(true);
    try {
      const isEdit = editing?.item?.id;
      const path = {
        loan: "/loans",
        insurance: "/insurance",
        investment: "/investments",
        family: "/family",
        asset: "/assets",
        card: "/cards",
      }[kind];
      if (isEdit) {
        await api.put(`/clients/${id}${path}/${editing.item.id}`, body);
        toast.success("Updated");
      } else {
        await api.post(`/clients/${id}${path}`, body);
        toast.success("Added");
      }
      setEditing(null);
      setC360Key((k) => k + 1);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const { kind, id: rid } = confirmDel;
    try {
      const path = { loan: "/loans", insurance: "/insurance", investment: "/investments", family: "/family", asset: "/assets", card: "/cards", client: "" }[kind];
      if (kind === "client") {
        await api.delete(`/clients/${id}`);
        toast.success("Client deleted");
        navigate("/app/clients");
        return;
      }
      await api.delete(`/clients/${id}${path}/${rid}`);
      toast.success("Deleted");
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <Link to="/app/clients" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-emerald-900" data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </Link>

      <div className="bg-emerald-900 text-white rounded-2xl p-6 lg:p-8 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-60 h-60 bg-amber-500/10 rounded-full" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{client.name}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-emerald-100">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {client.mobile}</span>
              {client.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {client.email}</span>}
              {client.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {client.city}</span>}
              {client.occupation && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {client.occupation}{client.employer ? ` · ${client.employer}` : ""}</span>}
              {client.dob && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> DOB {client.dob}{insights?.age ? ` · ${insights.age} yrs` : ""}</span>}
            </div>
            {client.annual_income ? (
              <div className="text-sm text-emerald-100 mt-2">Annual income: <span className="font-bold text-white">{formatINR(client.annual_income)}</span></div>
            ) : (
              <div className="text-xs text-amber-200 mt-2 italic">Add annual income on this client to unlock personalised cover recommendations.</div>
            )}
            {client.notes && <div className="mt-3 text-xs italic text-emerald-200 max-w-2xl">{client.notes}</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to={`/app/clients/${id}/discovery`}>
              <Button variant="outline"
                className="bg-emerald-900 border-amber-400/60 text-amber-300 hover:bg-emerald-800 hover:text-amber-200 font-bold gap-2" data-testid="open-discovery-btn">
                <Radar className="w-4 h-4" />
                <span className="hidden sm:inline">Insurance Discovery</span>
              </Button>
            </Link>
            <Button onClick={() => setNaOpen(true)} variant="outline"
              className="bg-emerald-900 border-emerald-700 text-amber-300 hover:bg-emerald-800 hover:text-amber-200 font-bold gap-2" data-testid="open-need-analysis-btn">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Need Analysis</span>
            </Button>
            <Button onClick={downloadReport} disabled={downloading} variant="outline"
              className="bg-amber-400 border-amber-400 text-emerald-950 hover:bg-amber-300 hover:text-emerald-950 font-bold gap-2" data-testid="download-report-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">Snapshot PDF</span>
            </Button>
            <Button onClick={() => setConfirmDel({ kind: "client", id, label: "this client" })} variant="outline"
              className="bg-transparent border-red-300 text-red-200 hover:bg-red-900/30 hover:text-white" data-testid="delete-client-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="flex border-b border-stone-200 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition ${tab === t.key ? "border-emerald-900 text-emerald-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
              {t.label}
              <span className="ml-2 text-xs text-stone-400">
                {t.key === "loans" && loans.length}
                {t.key === "cards" && cards.length}
                {t.key === "insurance" && insurance.length}
                {t.key === "investments" && investments.length}
                {t.key === "family" && family.length}
                {t.key === "crece" && crece_products.length}
                {t.key === "dashboard" && <Sparkles className="w-3 h-3 inline" />}
              </span>
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "timeline" && <PolicyTimeline clientId={id} />}
          {tab === "appchat" && <OneChatPanel clientId={id} />}
          {tab === "discovered" && <DiscoveredPanel clientId={id} onConfirmed={() => { load(); setC360Key((k) => k + 1); }} />}
          {tab === "dashboard" && (
            <div className="space-y-5">
              <Customer360Card clientId={id} refreshKey={c360Key} />
              <DashboardView insights={insights} client={client} crece={crece_products} loans={loans} insurance={insurance} investments={investments} navigate={navigate} />
            </div>
          )}

          {tab === "cards" && (
            <div>
              <div className="flex justify-end gap-2 mb-4">
                <Button variant="outline" onClick={() => setShowCardLens((v) => !v)} className="gap-2 border-emerald-900 text-emerald-900" data-testid="client-card-lens-btn"><Sparkles className="w-4 h-4" /> {showCardLens ? "Hide Card Lens" : "Card Lens — best card for their habits"}</Button>
                <Button onClick={() => setEditing({ kind: "card" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="add-card-btn"><Plus className="w-4 h-4" /> Add Card</Button>
              </div>
              {showCardLens && <div className="mb-5 rounded-2xl bg-[#FBF8F1] p-4" data-testid="client-card-lens"><CardLensPanel clientId={id} /></div>}
              {cards.length === 0 ? <Empty label="No credit cards recorded — add one or confirm a discovered card bill" /> : (
                <div className="space-y-3">
                  {cards.map((k) => (
                    <div key={k.id} className="border border-stone-200 rounded-lg p-4" data-testid={`card-row-${k.id}`}>
                      <div className="flex justify-between gap-4 flex-wrap">
                        <div>
                          <div className="font-bold">{k.issuer} {k.card_name || "card"}{k.last4 ? <span className="font-mono text-stone-500"> ··{k.last4}</span> : null}
                            <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${k.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>{k.status}</span>
                          </div>
                          <div className="text-xs text-stone-500 mt-0.5">{ownerName(k, family)}{k.reward_type ? ` · ${k.reward_type}` : ""}</div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "card", item: k })}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "card", id: k.id, label: `${k.issuer} card` })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                        <Stat label="Credit limit" value={formatINR(k.credit_limit || 0)} />
                        <Stat label="Billing / due day" value={`${k.billing_day || "—"} / ${k.due_day || "—"}`} />
                        <Stat label="Annual fee" value={`${formatINR(k.annual_fee || 0)}${k.annual_fee_date ? ` · ${k.annual_fee_date}` : ""}`} />
                        <Stat label="Added" value={(k.created_at || "").slice(0, 10)} />
                      </div>
                      {k.notes && <div className="text-xs text-stone-500 italic mt-2">{k.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "loans" && (
            <div>
              <div className="flex justify-end mb-4">
                <Button onClick={() => setEditing({ kind: "loan" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="add-loan-btn">
                  <Plus className="w-4 h-4" /> Add Loan
                </Button>
              </div>
              {loans.length === 0 ? <Empty label="No loans recorded" /> : (
                <div className="space-y-3">
                  {loans.map((l) => {
                    const assetVal = (l.asset_value != null) ? l.asset_value
                      : (ASSET_BACKED_LOAN_TYPES.has(l.loan_type) ? (l.sanctioned_amount || 0) : 0);
                    return (
                    <div key={l.id} className="border border-stone-200 rounded-lg p-4" data-testid={`loan-row-${l.id}`}>
                      <div className="flex justify-between gap-4 flex-wrap">
                        <div>
                          <div className="font-bold">{l.bank} · {l.loan_type}
                            {assetVal > 0 && (
                              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider">Asset {formatINR(assetVal)}</span>
                            )}
                          </div>
                          <div className="text-xs text-stone-500 mt-0.5">{ownerName(l, family)}{l.account_no ? ` · A/C ${l.account_no}` : ""}</div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "loan", item: l })}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "loan", id: l.id, label: `${l.bank} loan` })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                        <Stat label="Outstanding" value={formatINR(l.outstanding || 0)} />
                        <Stat label="EMI" value={`${formatINR(l.emi || 0)}${l.emi_day ? ` · day ${l.emi_day}` : ""}`} />
                        <Stat label="Sanctioned" value={formatINR(l.sanctioned_amount || 0)} />
                        <Stat label="Rate / Tenure" value={`${l.interest_rate || "—"}% / ${l.tenure_months || "—"}m`} />
                      </div>
                      {l.notes && <div className="text-xs text-stone-500 italic mt-2">{l.notes}</div>}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "insurance" && (
            <div>
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex flex-wrap gap-2" data-testid="insurance-category-filter">
                  {["All", ...INSURANCE_CATEGORIES].map((cat) => {
                    const count = cat === "All" ? insurance.length : insurance.filter((i) => bucketOf(i.policy_type) === cat).length;
                    const Icon = cat === "Life" ? Heart : cat === "Health" ? ShieldAlert : cat === "Motor" ? Car : cat === "Other" ? FileText : Sparkles;
                    return (
                      <button key={cat} onClick={() => setInsuranceFilter(cat)} data-testid={`ins-filter-${cat.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition ${insuranceFilter === cat ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                        <Icon className="w-3 h-3" /> {cat} <span className="opacity-70">{count}</span>
                      </button>
                    );
                  })}
                </div>
                <Button onClick={() => setEditing({ kind: "insurance" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="add-insurance-btn">
                  <Plus className="w-4 h-4" /> Add Policy
                </Button>
              </div>
              {(() => {
                const filtered = insuranceFilter === "All" ? insurance : insurance.filter((i) => bucketOf(i.policy_type) === insuranceFilter);
                if (filtered.length === 0) return <Empty label={insuranceFilter === "All" ? "No insurance policies" : `No ${insuranceFilter} policies`} />;
                return (
                <div className="space-y-3">
                  {filtered.map((l) => {
                    const due = l.due_date ? new Date(l.due_date) : null;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const overdue = due && due < today;
                    return (
                      <div key={l.id} className="border border-stone-200 rounded-lg p-4" data-testid={`ins-row-${l.id}`}>
                        <div className="flex justify-between gap-4 flex-wrap">
                          <div>
                            <div className="font-bold flex items-center gap-2">{l.provider} · {l.policy_type}
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 uppercase tracking-wider">{bucketOf(l.policy_type)}</span>
                              {overdue && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue</span>}
                            </div>
                            <div className="text-xs text-stone-500 mt-0.5">{ownerName(l, family)}{l.policy_no ? ` · Policy ${l.policy_no}` : ""}</div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "insurance", item: l })}><Edit2 className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "insurance", id: l.id, label: `${l.provider} policy` })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                          <Stat label="Sum Assured" value={formatINR(l.sum_assured || 0)} />
                          <Stat label="Premium" value={`${formatINR(l.premium || 0)} / ${l.premium_frequency || "—"}`} />
                          <Stat label="Next Due" value={l.due_date || "—"} />
                          <Stat label="Nominee" value={l.nominee || "—"} />
                        </div>
                        {l.notes && <div className="text-xs text-stone-500 italic mt-2">{l.notes}</div>}
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>
          )}

          {tab === "investments" && (
            <div>
              <div className="flex justify-end mb-4">
                <Button onClick={() => setEditing({ kind: "investment" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="add-investment-btn">
                  <Plus className="w-4 h-4" /> Add Investment
                </Button>
              </div>
              {investments.length === 0 ? <Empty label="No investments recorded" /> : (
                <div className="space-y-3">
                  {investments.map((l) => (
                    <div key={l.id} className="border border-stone-200 rounded-lg p-4" data-testid={`inv-row-${l.id}`}>
                      <div className="flex justify-between gap-4 flex-wrap">
                        <div>
                          <div className="font-bold">{l.instrument}{l.provider ? ` · ${l.provider}` : ""}</div>
                          <div className="text-xs text-stone-500 mt-0.5">{ownerName(l, family)}{l.folio_no ? ` · Folio ${l.folio_no}` : ""}</div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "investment", item: l })}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "investment", id: l.id, label: `${l.instrument} investment` })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                        <Stat label="Invested" value={formatINR(l.amount_invested || 0)} />
                        <Stat label="Current Value" value={formatINR(l.current_value || 0)} />
                        <Stat label="SIP" value={l.sip_amount ? `${formatINR(l.sip_amount)}${l.sip_day ? ` · day ${l.sip_day}` : ""}` : "—"} />
                        <Stat label="Maturity" value={l.maturity_date || "—"} />
                      </div>
                      {l.notes && <div className="text-xs text-stone-500 italic mt-2">{l.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "assets" && (
            <div data-testid="assets-tab">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-stone-500">Track non-financial assets (property, vehicle, gold) and pure-financial holdings (FD, MF, equity). Link any asset to a loan to mark it as collateral.</div>
                <Button onClick={() => setEditing({ kind: "asset" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2 shrink-0" data-testid="add-asset-btn">
                  <Plus className="w-4 h-4" /> Add Asset
                </Button>
              </div>
              {assetsList.length === 0 ? <Empty label="No assets added yet" /> : (
                <div className="space-y-3">
                  {assetsList.map((a) => {
                    const linked = a.linked_loan_id ? loans.find((l) => l.id === a.linked_loan_id) : null;
                    const kindLabel = (ASSET_KINDS.find((k) => k.k === a.kind) || {}).label || a.kind;
                    const ownerLabel = ownerName(a, family);
                    return (
                      <div key={a.id} className="border border-stone-200 rounded-lg p-4" data-testid={`asset-row-${a.id}`}>
                        <div className="flex justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-bold">{a.name}</div>
                            <div className="text-xs text-stone-500 mt-0.5">
                              <span className="bg-stone-100 px-2 py-0.5 rounded-full text-stone-700 mr-2">{kindLabel}</span>
                              {ownerLabel}
                              {a.location ? ` · ${a.location}` : ""}
                              {a.purchase_date ? ` · bought ${a.purchase_date}` : ""}
                            </div>
                            {linked && (
                              <div className="text-[11px] text-amber-800 mt-1">
                                <b>Collateral for:</b> {linked.bank} · {linked.loan_type} · outstanding ₹{Math.round(linked.outstanding || 0).toLocaleString("en-IN")}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "asset", item: a })}><Edit2 className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "asset", id: a.id, label: a.name })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 text-sm">
                          <Stat label="Current Value" value={formatINR(a.value || 0)} />
                          <Stat label="Purchase Value" value={a.purchase_value ? formatINR(a.purchase_value) : "—"} />
                          <Stat label="Gain / Loss" value={a.purchase_value ? `${(a.value || 0) >= a.purchase_value ? "+" : ""}${formatINR((a.value || 0) - a.purchase_value)}` : "—"} />
                        </div>
                        {a.notes && <div className="text-xs text-stone-500 italic mt-2">{a.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "family" && (
            <div>
              <div className="flex justify-end mb-4">
                <Button onClick={() => setEditing({ kind: "family" })} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="add-family-btn">
                  <Plus className="w-4 h-4" /> Add Family Member
                </Button>
              </div>
              {family.length === 0 ? <Empty label="No family members linked" /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {family.map((f) => {
                    const memberLoans = loans.filter((x) => x.family_member_id === f.id).length;
                    const memberIns = insurance.filter((x) => x.family_member_id === f.id).length;
                    const memberInv = investments.filter((x) => x.family_member_id === f.id).length;
                    return (
                      <div key={f.id} className="border border-stone-200 rounded-lg p-4" data-testid={`family-row-${f.id}`}>
                        <div className="flex justify-between gap-2">
                          <div>
                            <div className="font-bold">{f.name}</div>
                            <div className="text-xs text-stone-500 capitalize">{f.relation}{f.dob ? ` · DOB ${f.dob}` : ""}</div>
                            {f.mobile && <div className="text-xs text-stone-500 mt-0.5"><Phone className="w-3 h-3 inline mr-1" />{f.mobile}</div>}
                            {f.occupation && <div className="text-xs text-stone-500 mt-0.5"><Briefcase className="w-3 h-3 inline mr-1" />{f.occupation}</div>}
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "family", item: f })}><Edit2 className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="outline" onClick={() => setConfirmDel({ kind: "family", id: f.id, label: f.name })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500 flex gap-4">
                          <span><b className="text-stone-800">{memberLoans}</b> loans</span>
                          <span><b className="text-stone-800">{memberIns}</b> insurance</span>
                          <span><b className="text-stone-800">{memberInv}</b> investments</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "crece" && (
            <div>
              {crece_products.length === 0 ? (
                <div className="text-center py-10" data-testid="crece-empty-cta">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p className="font-semibold text-stone-700">No Crece products yet</p>
                  <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                    This client hasn&apos;t purchased a Crece product. Recommend one from our catalog and submit a lead in their name.
                  </p>
                  <Button onClick={() => navigate("/app/products")} className="mt-4 bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="recommend-product-btn">
                    <ShoppingBag className="w-4 h-4" /> Browse products
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {crece_products.map((cp) => (
                    <div key={cp.id} className="border border-stone-200 rounded-lg p-4 flex items-center justify-between" data-testid={`crece-row-${cp.id}`}>
                      <div>
                        <div className="font-bold">{cp.product_name}</div>
                        <div className="text-xs text-stone-500">{cp.product_category} · Linked {(cp.linked_at || "").slice(0, 10)}</div>
                      </div>
                      <span className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold">{cp.lead_status}</span>
                    </div>
                  ))}
                  <Button onClick={() => navigate("/app/products")} variant="outline" className="w-full mt-4 gap-2" data-testid="recommend-more-products-btn">
                    <Plus className="w-4 h-4" /> Recommend another product
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <FormModal open={editing?.kind === "card"} title={editing?.item ? "Edit card" : "Add credit card"} description="Issuer, limit, billing cycle and annual fee — feeds Smart Card Match and the renewals list" onClose={() => setEditing(null)}>
        <CardForm initial={editing?.item} family={family} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("card", b)} />
      </FormModal>
      <FormModal open={editing?.kind === "loan"} title={editing?.item ? "Edit loan" : "Add loan"} description="Track this client's loan across any bank" onClose={() => setEditing(null)}>
        <LoanForm initial={editing?.item} family={family} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("loan", b)} />
      </FormModal>
      <FormModal open={editing?.kind === "insurance"} title={editing?.item ? "Edit insurance" : "Add insurance"} description="Policy details across life, health, motor and more" onClose={() => setEditing(null)}>
        <InsuranceForm initial={editing?.item} family={family} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("insurance", b)} />
      </FormModal>
      <FormModal open={editing?.kind === "investment"} title={editing?.item ? "Edit investment" : "Add investment"} description="MF, FD, PPF, stocks and other investments" onClose={() => setEditing(null)}>
        <InvestmentForm initial={editing?.item} family={family} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("investment", b)} />
      </FormModal>
      <FormModal open={editing?.kind === "family"} title={editing?.item ? "Edit family member" : "Add family member"} description="Link a dependent and track their own mini-portfolio" onClose={() => setEditing(null)}>
        <FamilyForm initial={editing?.item} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("family", b)} />
      </FormModal>
      <FormModal open={editing?.kind === "asset"} title={editing?.item ? "Edit asset" : "Add asset"} description="Track property, vehicles, FDs, equity and other holdings — link to a loan to mark as collateral." onClose={() => setEditing(null)}>
        <AssetForm initial={editing?.item} family={family} loans={loans} saving={saving} onCancel={() => setEditing(null)} onSave={(b) => handleSave("asset", b)} />
      </FormModal>

      <NeedAnalysisWizard
        open={naOpen}
        onClose={() => setNaOpen(false)}
        clientId={id}
        initial={{
          monthly_take_home: client.annual_income ? Math.round(client.annual_income / 12) : 75000,
          marital_status: family.length === 0 ? "Single" : (family.some(f => /child|son|daughter|kid/i.test(f.relation)) ? "Married with kids" : "Married"),
          num_kids: family.filter(f => /child|son|daughter|kid/i.test(f.relation)).length,
          kid_ages: family.filter(f => /child|son|daughter|kid/i.test(f.relation)).map(f => f.age || 5),
          outstanding_loans: loans.reduce((s, l) => s + (l.outstanding || 0), 0),
          city_tier: ["Mumbai", "Delhi", "Bangalore", "Bengaluru", "Chennai", "Kolkata", "Hyderabad"].includes(client.city || "") ? "Metro" : "Tier-1",
        }}
      />

      <ConfirmDelete open={!!confirmDel} title={`Delete ${confirmDel?.label || ""}?`} onCancel={() => setConfirmDel(null)} onConfirm={handleDelete} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-stone-500">{label}</div>
      <div className="font-semibold text-stone-800">{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-center py-12 text-stone-400 text-sm">{label}</div>;
}

// ---------- Dashboard view ----------
function KpiCard({ icon: Icon, label, value, tone = "emerald", sub }) {
  const tones = {
    emerald: "from-emerald-900 to-emerald-800 text-amber-200",
    amber: "from-amber-600 to-amber-700 text-amber-100",
    rose: "from-rose-700 to-rose-800 text-rose-100",
    blue: "from-blue-800 to-blue-900 text-blue-100",
    violet: "from-violet-800 to-violet-900 text-violet-100",
    slate: "from-stone-800 to-stone-900 text-stone-200",
  };
  return (
    <div className={`bg-gradient-to-br ${tones[tone]} rounded-2xl p-5 text-white relative overflow-hidden`}>
      <div className="absolute -right-6 -bottom-6 opacity-10"><Icon className="w-24 h-24" /></div>
      <div className="relative">
        <div className="flex items-center gap-2 opacity-80">
          <Icon className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold">{label}</span>
        </div>
        <div className="font-display text-2xl lg:text-3xl font-black mt-2 text-white">{value}</div>
        {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function CoverageBar({ label, have, needed, gap, pct, color, icon: Icon }) {
  const pctSafe = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`coverage-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg ${color.bg} ${color.text} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
          <div>
            <div className="font-display font-bold text-lg">{label} Cover</div>
            <div className="text-xs text-stone-500">{pctSafe}% covered</div>
          </div>
        </div>
        {gap > 0 ? (
          <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-bold inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Gap {formatINRShort(gap)}
          </span>
        ) : needed > 0 ? (
          <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold">Fully covered</span>
        ) : (
          <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 font-bold">Add income to compute</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-500">Already have</div>
          <div className="font-bold text-lg text-emerald-900">{formatINRShort(have)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-500">Recommended</div>
          <div className="font-bold text-lg text-stone-800">{formatINRShort(needed)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-500">Gap</div>
          <div className={`font-bold text-lg ${gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{formatINRShort(gap)}</div>
        </div>
      </div>
      <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full ${color.bar} transition-all duration-700`} style={{ width: `${pctSafe}%` }} />
      </div>
    </div>
  );
}

function Donut({ slices, total, label }) {
  // Build a conic-gradient pie chart with each slice's % of total
  const palette = ["#064E3B", "#9F1239", "#0369A1", "#6D28D9"]; // life, health, motor, other
  const segments = [];
  let acc = 0;
  slices.forEach((s, i) => {
    if (!s.value || total <= 0) return;
    const pct = (s.value / total) * 100;
    segments.push(`${palette[i % palette.length]} ${acc}% ${acc + pct}%`);
    acc += pct;
  });
  if (acc < 100 && acc > 0) segments.push(`#F5F5F4 ${acc}% 100%`);
  if (segments.length === 0) segments.push(`#F5F5F4 0 100%`);
  return (
    <div className="flex items-center gap-5">
      <div className="relative shrink-0">
        <div className="w-32 h-32 rounded-full" style={{ background: `conic-gradient(${segments.join(", ")})` }} />
        <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
          <div className="text-[10px] uppercase text-stone-500 tracking-wide">{label}</div>
          <div className="font-display font-black text-sm leading-tight text-center mt-0.5">{formatINR(total)}</div>
        </div>
      </div>
      <div className="space-y-1.5 flex-1 min-w-0">
        {slices.map((s, i) => (
          <div key={s.label} className="flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: palette[i % palette.length] }} />
              <span className="font-semibold truncate">{s.label}</span>
            </div>
            <span className="font-mono text-stone-700">{formatINR(s.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardView({ insights, client, crece, loans, insurance, investments, navigate }) {
  if (!insights) return <div className="text-center py-12 text-stone-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const t = insights.totals;
  const rec = insights.recommendations;
  const cats = insights.insurance_by_category;
  const insuranceTotal = (cats.Life.sum_assured + cats.Health.sum_assured + cats.Motor.sum_assured + cats.Other.sum_assured) || 0;
  const dti = rec.debt_to_income_pct || 0;
  const dtiColor = dti > 40 ? "text-red-600" : dti > 30 ? "text-amber-600" : "text-emerald-700";
  const dtiBar = dti > 40 ? "bg-red-500" : dti > 30 ? "bg-amber-500" : "bg-emerald-700";

  return (
    <div className="space-y-6" data-testid="dashboard-tab-content">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Wallet} label="Net Worth" tone={t.net_worth >= 0 ? "emerald" : "slate"}
          value={formatINRShort(t.net_worth)} sub={`Assets ${formatINRShort(t.total_assets)} − Liab ${formatINRShort(t.liabilities)}`} />
        <KpiCard icon={TrendingDown} label="Total Liabilities" tone="rose"
          value={formatINRShort(t.liabilities)} sub={`${formatINRShort(t.monthly_emi)}/mo EMI`} />
        <KpiCard icon={ShieldAlert} label="Total Cover" tone="amber"
          value={formatINRShort(t.life_cover + t.health_cover + t.motor_cover + t.other_cover)} sub={`${formatINRShort(t.annual_premium)}/yr premium`} />
        <KpiCard icon={TrendingUpIcon} label="Investments" tone="violet"
          value={formatINRShort(t.investments_current)} sub={`Invested ${formatINRShort(t.investments_invested)}`} />
      </div>

      {t.loan_assets > 0 && (
        <div className="text-xs text-stone-500 -mt-2 px-1">
          <Sparkles className="w-3 h-3 inline mr-1 text-amber-600" />
          Net Worth includes <b className="text-emerald-800">{formatINR(t.loan_assets)}</b> of loan-backed assets (home, car, etc.) + <b className="text-emerald-800">{formatINR(t.investments_current)}</b> investments − <b className="text-rose-700">{formatINR(t.liabilities)}</b> outstanding liabilities.
        </div>
      )}

      {/* Coverage gap (life + health) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CoverageBar label="Life" have={t.life_cover} needed={rec.life_cover_needed} gap={rec.life_cover_gap}
          pct={rec.life_coverage_pct} icon={Heart}
          color={{ bg: "bg-rose-100", text: "text-rose-700", bar: "bg-gradient-to-r from-rose-600 to-rose-400" }} />
        <CoverageBar label="Health" have={t.health_cover} needed={rec.health_cover_needed} gap={rec.health_cover_gap}
          pct={rec.health_coverage_pct} icon={ShieldAlert}
          color={{ bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-gradient-to-r from-emerald-700 to-emerald-400" }} />
      </div>

      {/* Insurance category donut + Loans summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="insurance-donut">
          <h3 className="font-display font-bold text-lg mb-4">Insurance by Category</h3>
          {insuranceTotal === 0 ? (
            <div className="text-sm text-stone-400 py-10 text-center">No insurance policies yet — add a policy to see the breakdown.</div>
          ) : (
            <Donut total={insuranceTotal} label="Total Cover"
              slices={[
                { label: `Life (${cats.Life.count})`, value: cats.Life.sum_assured },
                { label: `Health (${cats.Health.count})`, value: cats.Health.sum_assured },
                { label: `Motor (${cats.Motor.count})`, value: cats.Motor.sum_assured },
                { label: `Other (${cats.Other.count})`, value: cats.Other.sum_assured },
              ]} />
          )}
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <h3 className="font-display font-bold text-lg mb-4">Debt Health</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <div className="text-xs uppercase tracking-wide text-stone-500 font-semibold">Debt-to-Income</div>
                <div className={`font-display font-black text-2xl ${dtiColor}`} data-testid="dti-pct">{dti.toFixed(1)}%</div>
              </div>
              <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                <div className={`h-full ${dtiBar} transition-all duration-700`} style={{ width: `${Math.min(100, dti)}%` }} />
              </div>
              <div className="text-[11px] text-stone-500 mt-1.5">
                {dti > 40 ? "Over-leveraged. Avoid new EMI commitments." : dti > 30 ? "Stretched — be cautious with new loans." : insights.annual_income > 0 ? "Comfortable — room for measured borrowing." : "Add the client's annual income to compute this."}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-stone-100">
              <div><div className="text-[10px] uppercase text-stone-500 tracking-wide">Monthly EMI</div><div className="font-bold text-lg">{formatINR(t.monthly_emi)}</div></div>
              <div><div className="text-[10px] uppercase text-stone-500 tracking-wide">Sanctioned</div><div className="font-bold text-lg">{formatINR(t.sanctioned_total)}</div></div>
              <div><div className="text-[10px] uppercase text-stone-500 tracking-wide">Outstanding</div><div className="font-bold text-lg text-rose-700">{formatINR(t.liabilities)}</div></div>
              <div><div className="text-[10px] uppercase text-stone-500 tracking-wide">Loans Count</div><div className="font-bold text-lg">{loans.length}</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Crece product CTA */}
      <div className={`rounded-2xl p-5 ${crece.length === 0 ? "bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`} data-testid="crece-cta">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <ShoppingBag className={`w-10 h-10 ${crece.length === 0 ? "text-amber-700" : "text-emerald-700"}`} />
            <div>
              <div className="font-display font-black text-lg">
                {crece.length === 0 ? "No Crece products yet" : `${crece.length} Crece product${crece.length > 1 ? "s" : ""} purchased`}
              </div>
              <div className="text-sm text-stone-700">
                {crece.length === 0
                  ? "This client hasn't bought any Crece-listed product. Recommend something to plug the cover gap."
                  : "Keep recommending — every closed lead earns you commission and improves this client's portfolio."}
              </div>
            </div>
          </div>
          <Button onClick={() => navigate("/app/products")} className="bg-emerald-900 hover:bg-emerald-800 gap-2" data-testid="dashboard-browse-products-btn">
            <ShoppingBag className="w-4 h-4" /> Browse products
          </Button>
        </div>
      </div>

      {/* Quick portfolio summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portfolio-summary-grid">
        <SummaryCard tone="rose" label="Active Loans" value={loans.length} sub={`${formatINR(t.liabilities)} outstanding`} />
        <SummaryCard tone="amber" label="Insurance Policies" value={insurance.length} sub={`${formatINR(t.life_cover + t.health_cover + t.motor_cover + t.other_cover)} cover`} />
        <SummaryCard tone="violet" label="Investments" value={investments.length} sub={`${formatINR(t.investments_current)} current value`} />
        <SummaryCard tone="emerald" label="Crece Products" value={crece.length} sub={crece.length === 0 ? "Recommend now" : `${crece.length} linked`} />
      </div>
    </div>
  );
}

function SummaryCard({ tone, label, value, sub }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    violet: "border-violet-200 bg-violet-50 text-violet-900",
  };
  return (
    <div className={`border rounded-xl p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-wide opacity-70">{label}</div>
      <div className="font-display font-black text-3xl mt-1">{value}</div>
      <div className="text-xs opacity-80 mt-1 truncate">{sub}</div>
    </div>
  );
}
